import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import { GlassCard } from '../../components/GlassCard';
import { FileSpreadsheet, Download, AlertTriangle, Calendar, FileText, Hash, IndianRupee } from 'lucide-react';

function getLast12Months() {
  const months = [];
  const now = new Date();
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    months.push({ value: val, label });
  }
  return months;
}

const CARDS = [
  { key: 'b2b', title: 'B2B Invoices', desc: 'Registered customers, invoice-wise. Includes CGST/SGST/IGST split as trailing columns.', color: 'brand-purple', bg: 'bg-purple-50', border: 'border-purple-200', iconColor: 'text-brand-purple' },
  { key: 'b2cl', title: 'B2CL Invoices', desc: 'Large B2C invoices (interstate, over ₹2.5L).', color: 'teal', bg: 'bg-teal-50', border: 'border-teal-200', iconColor: 'text-teal-600' },
  { key: 'hsn', title: 'HSN Summary', desc: 'HSN-wise aggregation for the month.', color: 'amber', bg: 'bg-amber-50', border: 'border-amber-200', iconColor: 'text-amber-600' },
];

export default function GSTR1() {
  const { token, tenant } = useAuth();
  const toast = useToast();
  const { formatMoney } = useTenantFormat();

  const months = useMemo(() => getLast12Months(), []);
  const [month, setMonth] = useState(months[0]?.value || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inOnly, setInOnly] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    if (!token || !month) return;
    setLoading(true);
    setInOnly(false);
    try {
      const r = await api.gstr1Summary(token, month);
      setData(r);
    } catch (e) {
      const code = e?.detail?.detail?.code || '';
      const msg = e?.message || '';
      if (e.status === 400 && (code === 'in_only' || msg.includes('India-specific') || msg.includes('in_only'))) {
        setInOnly(true);
        setData(null);
      } else if (e.status === 403) {
        setData({ _upgrade: true });
      } else if (e.status !== 401) {
        toast.show(e.message || 'Failed to load', 'error');
      }
    } finally { setLoading(false); }
  }, [token, month, toast]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async (kind) => {
    setDownloading(kind);
    try {
      const BASE = process.env.REACT_APP_BACKEND_URL || '';
      const paths = { b2b: 'b2b', b2cl: 'b2cl', hsn: 'hsn-summary' };
      const url = `${BASE}/api/erp/gstr1/${paths[kind]}?month=${month}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.detail?.message || err?.detail || `Download failed (${resp.status})`);
      }
      const blob = await resp.blob();
      // Extract filename from Content-Disposition or fallback
      const disp = resp.headers.get('content-disposition') || '';
      const match = disp.match(/filename="?([^"]+)"?/);
      const slug = tenant?.slug || 'tenant';
      const fallback = `gstr1-${kind}-${slug}-${month}.csv`;
      const filename = match?.[1] || fallback;

      // Trigger download via hidden <a>
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.show(`Downloaded ${filename}`, 'success');
    } catch (e) {
      toast.show(e.message || 'Download failed', 'error');
    } finally { setDownloading(null); }
  };

  const selectedLabel = months.find(m => m.value === month)?.label || month;

  if (data?._upgrade) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="gstr1-page">
        <GlassCard className="p-12 text-center">
          <AlertTriangle size={40} className="text-yellow-500 mx-auto mb-3" />
          <h2 className="text-gray-900 font-extrabold text-lg mb-2">Premium Feature</h2>
          <p className="text-gray-500 text-sm">GSTR-1 export requires a Pro or Premium plan.</p>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="gstr1-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-200 flex items-center justify-center">
            <FileSpreadsheet size={20} className="text-brand-purple" />
          </div>
          <div>
            <h1 className="text-gray-900 font-extrabold text-xl">GSTR-1 Export (India)</h1>
            <p className="text-gray-500 text-xs">Auditor-ready CSVs for your monthly GST return.</p>
          </div>
        </div>

        {/* Month picker */}
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-gray-400" />
          <select value={month} onChange={e => setMonth(e.target.value)} data-testid="gstr1-month-picker"
            className="h-10 px-3 pr-8 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm font-semibold focus:border-brand-purple outline-none appearance-none cursor-pointer">
            {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
      </div>

      {/* In-Only notice */}
      {inOnly && (
        <GlassCard className="p-6 bg-amber-50 border-amber-200 mb-6" data-testid="gstr1-in-only-notice">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-amber-800 font-bold text-sm">GSTR-1 is India-specific</h3>
              <p className="text-amber-700 text-xs mt-1">
                This report is only available for Indian (IN) tenants. Use the{' '}
                <Link to="/reports/tax" className="text-brand-purple font-bold hover:underline">Tax Report → Export</Link>{' '}
                to prepare your VAT return.
              </p>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Summary card */}
      {loading ? (
        <div className="h-32 rounded-2xl bg-gray-100 animate-pulse mb-6" />
      ) : data && !inOnly ? (
        <GlassCard className="p-5 mb-6" data-testid="gstr1-summary-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-900 font-extrabold text-lg">{selectedLabel}</p>
              <p className="text-gray-400 text-xs mt-0.5">As of {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
            </div>
            <div className="flex gap-4">
              <div className="text-center px-4 py-2 rounded-xl bg-purple-50 border border-purple-200">
                <FileText size={14} className="text-brand-purple mx-auto" />
                <p className="text-gray-900 font-extrabold text-lg tabular-nums mt-1" data-testid="gstr1-b2b-count">{data.b2b_count}</p>
                <p className="text-gray-500 text-[10px] font-bold uppercase">B2B Invoices</p>
              </div>
              <div className="text-center px-4 py-2 rounded-xl bg-teal-50 border border-teal-200">
                <FileText size={14} className="text-teal-600 mx-auto" />
                <p className="text-gray-900 font-extrabold text-lg tabular-nums mt-1" data-testid="gstr1-b2cl-count">{data.b2cl_count}</p>
                <p className="text-gray-500 text-[10px] font-bold uppercase">B2CL Invoices</p>
              </div>
              <div className="text-center px-4 py-2 rounded-xl bg-amber-50 border border-amber-200">
                <Hash size={14} className="text-amber-600 mx-auto" />
                <p className="text-gray-900 font-extrabold text-lg tabular-nums mt-1" data-testid="gstr1-hsn-count">{data.hsn_row_count}</p>
                <p className="text-gray-500 text-[10px] font-bold uppercase">HSN Rows</p>
              </div>
              <div className="text-center px-4 py-2 rounded-xl bg-emerald-50 border border-emerald-200">
                <IndianRupee size={14} className="text-emerald-600 mx-auto" />
                <p className="text-gray-900 font-extrabold text-lg tabular-nums mt-1" data-testid="gstr1-taxable">{formatMoney(data.total_taxable_value)}</p>
                <p className="text-gray-500 text-[10px] font-bold uppercase">Taxable Value</p>
                <p className="text-gray-400 text-[9px] mt-0.5">Tax: {formatMoney(data.total_tax_collected)}</p>
              </div>
            </div>
          </div>
        </GlassCard>
      ) : null}

      {/* Download cards */}
      {!inOnly && !loading && data && (
        <div className="space-y-3" data-testid="gstr1-download-cards">
          {CARDS.map(card => (
            <GlassCard key={card.key} className={`p-5 ${card.bg} ${card.border}`} data-testid={`gstr1-card-${card.key}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-xl bg-white/80 border border-white flex items-center justify-center flex-shrink-0`}>
                    <FileSpreadsheet size={18} className={card.iconColor} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-gray-900 font-bold text-sm">{card.title}</h3>
                    <p className="text-gray-500 text-xs mt-0.5">{card.desc}</p>
                  </div>
                </div>
                <button onClick={() => downloadCsv(card.key)}
                  disabled={downloading === card.key}
                  data-testid={`gstr1-download-${card.key}`}
                  className="flex items-center gap-2 px-5 h-10 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-bold hover:bg-gray-50 shadow-sm transition-all disabled:opacity-50 flex-shrink-0 ml-4">
                  {downloading === card.key ? (
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download size={14} />
                  )}
                  {downloading === card.key ? 'Downloading...' : 'Download CSV'}
                </button>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
}
